import { useState } from "react";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  MessageSquare, 
  Send, 
  Reply, 
  Search,
  Filter,
  User,
  Bot,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  Archive,
  Star,
  Paperclip,
  Mail,
  Loader2,
  BarChart3,
  Globe,
  Shield,
  Code
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { CreditCard, Lock } from "lucide-react";

export default function SendReport() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newMessageSubject, setNewMessageSubject] = useState("");
  const [newMessageContent, setNewMessageContent] = useState("");
  const [showEmailTest, setShowEmailTest] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [showDomainReports, setShowDomainReports] = useState(false);
  const [reportEmailAddress, setReportEmailAddress] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get current user to set default email addresses
  const { data: currentUser } = useQuery({
    queryKey: ["/api/user"],
  });

  // Get subscription status
  const { data: subscriptionStatus } = useQuery({
    queryKey: ["/api/subscription/status"],
  });

  // Set default email addresses to user's email when user data loads
  React.useEffect(() => {
    if (currentUser?.email) {
      if (!testEmailAddress) {
        setTestEmailAddress(currentUser.email);
      }
      if (!reportEmailAddress) {
        setReportEmailAddress(currentUser.email);
      }
    }
  }, [currentUser, testEmailAddress, reportEmailAddress]);

  const { data: messagesData, isLoading } = useQuery({
    queryKey: ["/api/messages"],
    refetchInterval: 30000,
  });

  const { data: conversationData } = useQuery({
    queryKey: ["/api/messages", selectedConversation],
    enabled: !!selectedConversation,
  });

  const sendMessageMutation = useMutation({
    mutationFn: async (data: { conversationId?: string; subject?: string; content: string }) => {
      return apiRequest("/api/messages", "POST", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/messages"] });
      setNewMessage("");
      setNewMessageSubject("");
      setNewMessageContent("");
      setShowNewMessage(false);
      toast({ title: "Message sent successfully" });
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      return apiRequest(`/api/messages/${conversationId}/read`, "PUT");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/messages"] });
    },
  });

  const testEmailMutation = useMutation({
    mutationFn: async (emailAddress: string) => {
      return apiRequest("/api/send-domain-reports", {
        method: "POST",
        body: { email: emailAddress, reportType: 'comprehensive' }
      });
    },
    onSuccess: (data) => {
      toast({
        title: "Security Report Sent!",
        description: `Comprehensive security report sent to ${data.recipient || testEmailAddress}`,
      });
      setShowEmailTest(false);
    },
    onError: (error: any) => {
      if (error?.status === 403 && error?.data?.isTrial) {
        toast({
          title: "Paid Feature",
          description: "Sending reports is only available for paid subscribers. Please upgrade your plan.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Report Sending Failed",
          description: error.message || "Failed to send security report",
          variant: "destructive",
        });
      }
    },
  });

  const domainReportsMutation = useMutation({
    mutationFn: async (emailAddress: string) => {
      return apiRequest("/api/send-domain-reports", {
        method: "POST",
        body: { email: emailAddress, reportType: 'comprehensive' }
      });
    },
    onSuccess: (data) => {
      toast({
        title: "Domain Reports Sent!",
        description: `Comprehensive reports for ${data.domainsIncluded} domains sent to ${data.recipient}`,
      });
      setShowDomainReports(false);
    },
    onError: (error: any) => {
      if (error?.status === 403 && error?.data?.isTrial) {
        toast({
          title: "Paid Feature",
          description: "Sending reports is only available for paid subscribers. Please upgrade your plan.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Domain Reports Failed",
          description: error.message || "Failed to send domain reports",
          variant: "destructive",
        });
      }
    },
  });

  const conversations = Array.isArray(messagesData?.conversations) ? messagesData.conversations : [];
  const messages = Array.isArray(conversationData?.messages) ? conversationData.messages : [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "unread": return "bg-blue-100 text-blue-800 border-blue-200";
      case "read": return "bg-green-100 text-green-800 border-green-200";
      case "archived": return "bg-slate-100 text-slate-800 border-slate-200";
      case "important": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const getSenderIcon = (sender: string) => {
    return sender === "system" ? <Bot className="h-4 w-4" /> : <User className="h-4 w-4" />;
  };

  const filteredConversations = conversations.filter((conversation: any) => {
    const matchesSearch = 
      conversation.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversation.lastMessage?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversation.participants?.some((p: string) => p.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterStatus === "all" || conversation.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleSendMessage = () => {
    if (selectedConversation && newMessage.trim()) {
      sendMessageMutation.mutate({
        conversationId: selectedConversation,
        content: newMessage
      });
    }
  };

  const handleNewMessage = () => {
    if (newMessageSubject.trim() && newMessageContent.trim()) {
      sendMessageMutation.mutate({
        subject: newMessageSubject,
        content: newMessageContent
      });
    }
  };

  const unreadCount = conversations.filter((c: any) => c.status === "unread").length;

  // Show upgrade prompt for trial users
  if (subscriptionStatus?.isTrial && !subscriptionStatus?.hasActiveSubscription) {
    return (
      <div className="h-screen flex flex-col">
        <div className="flex-1 p-6">
          <Card className="border-2 border-yellow-500 max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-600">
                <Lock className="h-5 w-5" />
                Paid Feature - Upgrade Required
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600 dark:text-slate-400">
                Sending security reports via email is a premium feature available only for paid subscribers. 
                Trial users can view reports in the dashboard, but email delivery requires a subscription.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Upgrade to unlock:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                  <li>Email security reports</li>
                  <li>Automated report delivery</li>
                  <li>Comprehensive domain analysis reports</li>
                  <li>Priority support</li>
                </ul>
              </div>
              <Link href="/subscription">
                <Button className="w-full sm:w-auto">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Upgrade to Paid Plan
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Send Report
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Send security reports and domain analysis via email
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDomainReports(true)}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Domain Reports
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowEmailTest(true)}
          >
            <Mail className="h-4 w-4 mr-2" />
            Send Report
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewMessage(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Message
          </Button>
          <Badge variant="secondary" className="text-xs">
            <MessageSquare className="h-3 w-3 mr-1" />
            {unreadCount} Unread
          </Badge>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Conversations Sidebar */}
        <div className="w-1/3 border-r flex flex-col">
          {/* Search and Filter */}
          <div className="p-4 border-b space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Messages</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="important">Important</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-b">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Send Reports</span>
              </div>
              <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                Send security reports via email
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowEmailTest(true)}
                className="w-full text-xs"
              >
                <Mail className="h-3 w-3 mr-1" />
                Send Report
              </Button>
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                <span className="ml-2 text-sm text-slate-600">Loading...</span>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <div className="text-sm">No conversations found</div>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {filteredConversations.map((conversation: any) => (
                  <div
                    key={conversation.id}
                    onClick={() => {
                      setSelectedConversation(conversation.id);
                      if (conversation.status === "unread") {
                        markAsReadMutation.mutate(conversation.id);
                      }
                    }}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedConversation === conversation.id
                        ? "bg-emerald-100 dark:bg-purple-900/20"
                        : "hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        {getSenderIcon(conversation.type)}
                        <span className={`font-medium text-sm ${
                          conversation.status === "unread" ? "text-slate-900 dark:text-white" : "text-slate-600"
                        }`}>
                          {conversation.subject}
                        </span>
                        {conversation.status === "unread" && (
                          <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge className={`text-xs border ${getStatusColor(conversation.status)}`}>
                          {conversation.status}
                        </Badge>
                        {conversation.important && (
                          <Star className="h-3 w-3 text-yellow-500" />
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 truncate mb-1">
                      {conversation.lastMessage}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>From: {conversation.participants?.join(", ")}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(conversation.lastActivity).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Message Content Area */}
        <div className="flex-1 flex flex-col">
          {showDomainReports ? (
            /* Domain Reports Form */
            <div className="flex-1 p-6">
              <div className="max-w-2xl space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Send Domain Reports</h2>
                  <Button variant="outline" onClick={() => setShowDomainReports(false)}>
                    Cancel
                  </Button>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                      <BarChart3 className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-green-900 dark:text-green-100">
                        Comprehensive Domain Reports
                      </h3>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        Generate and send detailed security reports for all your monitored domains.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="report-email">Email Address</Label>
                  <Input
                    id="report-email"
                    type="email"
                    placeholder="Enter email address for reports..."
                    value={reportEmailAddress}
                    onChange={(e) => setReportEmailAddress(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Comprehensive security reports will be sent to this address
                  </p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Report Contents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-600 dark:text-slate-400">
                    <div className="flex items-center gap-2">
                      <Globe className="h-3 w-3" />
                      <span>All monitored domains</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-3 w-3" />
                      <span>Security compliance scores</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Code className="h-3 w-3" />
                      <span>JavaScript analysis</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-3 w-3" />
                      <span>Vulnerability assessments</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="h-3 w-3" />
                      <span>Security headers analysis</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="h-3 w-3" />
                      <span>Third-party domain tracking</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="h-3 w-3" />
                      <span>SSL certificate expiry dates</span>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => domainReportsMutation.mutate(reportEmailAddress)}
                  disabled={!reportEmailAddress.trim() || domainReportsMutation.isPending}
                  className="w-full"
                >
                  {domainReportsMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating Reports...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Send Domain Reports
                    </>
                  )}
                </Button>

                {domainReportsMutation.isSuccess && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <CheckCircle className="h-4 w-4" />
                      <p className="font-medium">Domain reports sent successfully!</p>
                    </div>
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      Comprehensive security reports have been sent to your email
                    </p>
                  </div>
                )}

                {domainReportsMutation.isError && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                      <AlertCircle className="h-4 w-4" />
                      <p className="font-medium">Domain reports failed</p>
                    </div>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                      Check your scan data and email configuration
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : showEmailTest ? (
            /* Email Test Form */
            <div className="flex-1 p-6">
              <div className="max-w-2xl space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Send Security Report</h2>
                  <Button variant="outline" onClick={() => setShowEmailTest(false)}>
                    Cancel
                  </Button>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                      <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Security Report Delivery
                      </h3>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Send a comprehensive security report to your email address.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="test-email">Email Address</Label>
                  <Input
                    id="test-email"
                    type="email"
                    placeholder="Enter email address to test..."
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    A comprehensive security report will be sent to this address
                  </p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium mb-2">Report Contents:</h4>
                  <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                    <li>• Security compliance scores for all domains</li>
                    <li>• JavaScript analysis and vulnerabilities</li>
                    <li>• Third-party domain tracking</li>
                    <li>• Security headers assessment</li>
                    <li>• SSL certificate expiry dates and status</li>
                    <li>• Professional HTML report formatting</li>
                  </ul>
                </div>

                <Button 
                  onClick={() => testEmailMutation.mutate(testEmailAddress)}
                  disabled={!testEmailAddress.trim() || testEmailMutation.isPending}
                  className="w-full"
                >
                  {testEmailMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending Report...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Security Report
                    </>
                  )}
                </Button>

                {testEmailMutation.isSuccess && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <CheckCircle className="h-4 w-4" />
                      <p className="font-medium">Security report sent successfully!</p>
                    </div>
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      Check your inbox for the comprehensive security report
                    </p>
                  </div>
                )}

                {testEmailMutation.isError && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                      <AlertCircle className="h-4 w-4" />
                      <p className="font-medium">Report sending failed</p>
                    </div>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                      Check your email configuration and scan data
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : showNewMessage ? (
            /* New Message Form */
            <div className="flex-1 p-6">
              <div className="max-w-2xl space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">New Message</h2>
                  <Button variant="outline" onClick={() => setShowNewMessage(false)}>
                    Cancel
                  </Button>
                </div>
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    placeholder="Enter message subject..."
                    value={newMessageSubject}
                    onChange={(e) => setNewMessageSubject(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="content">Message</Label>
                  <Textarea
                    id="content"
                    placeholder="Type your message here..."
                    value={newMessageContent}
                    onChange={(e) => setNewMessageContent(e.target.value)}
                    className="min-h-[200px]"
                  />
                </div>
                <Button 
                  onClick={handleNewMessage}
                  disabled={!newMessageSubject.trim() || !newMessageContent.trim() || sendMessageMutation.isPending}
                  className="w-full"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </Button>
              </div>
            </div>
          ) : selectedConversation ? (
            /* Selected Conversation */
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message: any) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.sender === "user"
                          ? "bg-purple-600 text-white"
                          : "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white"
                      }`}
                    >
                      <div className="text-sm">{message.content}</div>
                      <div className={`text-xs mt-1 ${
                        message.sender === "user" ? "text-emerald-100" : "text-slate-500"
                      }`}>
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="border-t p-4">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendMessageMutation.isPending}
                    size="sm"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            /* No Conversation Selected */
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <div className="text-lg font-medium mb-2">Select a conversation</div>
                <div className="text-sm">Choose a conversation from the list to view messages</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

